import { Container, Header, Box, Icon } from '@cloudscape-design/components';
import { useTranslation } from 'react-i18next';
import { Formatter } from "../../format";
import "./dashboard.css";

/**
 * Determina si la interaccion es texto o audio
 * @param {Object} data - ConversationAnalytics data
 * @returns {'text' | 'audio'}
 */
const getInteractionType = (data) => {
  const interactionType = data?.ConversationAnalytics?.SourceInformation?.[0]
    ?.TranscribeJobInfo?.InteractionType;
  return interactionType === 'text' ? 'text' : 'audio';
};

/**
 * Componente Badge para mostrar metadata
 * Formato: [Badge label] valor
 */
const MetadataBadge = ({ label, value, copyable = false, isLast = false }) => {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(String(value));
    }
  };

  return (
    <span className="metadata-item">
      <span className="metadata-badge">{label}</span>
      <span className="metadata-value">
        {value}
        {copyable && (
          <span className="metadata-copy-icon" onClick={handleCopy} title="Copiar">
            <Icon name="copy" size="small" />
          </span>
        )}
      </span>
      {!isLast && <span className="metadata-separator">/</span>}
    </span>
  );
};

/**
 * Header con badges de metadatos para el detalle de conversacion/llamada
 * @param {Object} props
 * @param {Object} props.data - Datos completos de la conversacion
 * @param {boolean} props.isLoading - Estado de carga
 */
export const ConversationDetailHeader = ({ data, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading || !data) {
    return (
      <Container
        header={
          <Header variant="h2">
            {t("callDetail.conversationTitle")}
          </Header>
        }
      >
        <Box>Cargando...</Box>
      </Container>
    );
  }

  const analytics = data?.ConversationAnalytics;
  const interactionType = getInteractionType(data);
  const isText = interactionType === 'text';

  // Titulo dinamico segun tipo de interaccion
  const title = isText
    ? t("callDetail.conversationTitle")
    : t("callDetail.callTitle");

  // Extraer campos de RegexMetadata y ConversationAnalytics
  const regexMetadata = analytics?.RegexMetadata || {};

  // Definir campos a mostrar con sus labels
  const fieldsConfig = [
    {
      key: 'titlebank',
      label: t("callDetail.metadata.name"),
      getValue: () => regexMetadata.titlebank || analytics?.TitleBank
    },
    {
      key: 'datetime',
      label: t("callDetail.metadata.date"),
      getValue: () => {
        const dt = regexMetadata.datetime || analytics?.ConversationTime;
        if (!dt) return null;
        // Formatear datetime si viene en formato especial
        if (dt.includes('T') && dt.includes('-')) {
          return dt.replace('T', ' ').replace(/-/g, (match, offset) => offset > 9 ? ':' : '-');
        }
        return dt.substring(0, 19);
      }
    },
    {
      key: 'guid',
      label: t("callDetail.metadata.guid"),
      getValue: () => regexMetadata.guid || analytics?.GUID,
      copyable: true
    },
    {
      key: 'duration',
      label: t("callDetail.metadata.duration"),
      getValue: () => analytics?.Duration ? Formatter.Time(analytics.Duration) : null
    },
    {
      key: 'ctype',
      label: t("callDetail.metadata.type"),
      getValue: () => {
        const ctype = regexMetadata.ctype || analytics?.TitleCallType;
        if (!ctype) return null;
        // Agregar indicador de texto/audio
        const typeLabel = isText ? t("interactionType.text") : t("interactionType.voice");
        return `${ctype} (${typeLabel})`;
      }
    },
    {
      key: 'agent',
      label: t("callDetail.metadata.agent"),
      getValue: () => regexMetadata.agent || analytics?.Agent
    },
    {
      key: 'tip',
      label: t("callDetail.metadata.typification"),
      getValue: () => regexMetadata.tip
    },
    {
      key: 'sup',
      label: t("callDetail.metadata.supervisor"),
      getValue: () => regexMetadata.sup || analytics?.TitleSupervisor
    },
    {
      key: 'cust',
      label: t("callDetail.metadata.customer"),
      getValue: () => regexMetadata.cust
    },
    {
      key: 'participants',
      label: t("callDetail.metadata.participants"),
      getValue: () => {
        const speakers = analytics?.SpeakerLabels;
        if (!speakers || speakers.length === 0) return null;
        return `${speakers.length} ${t("callDetail.metadata.participantsCount")}`;
      }
    },
  ];

  // Filtrar campos vacios y generar badges
  const badges = fieldsConfig
    .map(field => ({
      ...field,
      value: field.getValue()
    }))
    .filter(field =>
      field.value !== null &&
      field.value !== undefined &&
      field.value !== '' &&
      field.value !== 'None' &&
      field.value !== 'SIN-SUPERVISOR'
    );

  // Agregar campos dinamicos adicionales de RegexMetadata que no esten en la config
  const knownKeys = new Set(['titlebank', 'guid', 'agent', 'cust', 'tip', 'ctype', 'sup', 'datetime']);
  Object.entries(regexMetadata).forEach(([key, value]) => {
    if (!knownKeys.has(key) && value && value !== '' && value !== 'None') {
      badges.push({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        value
      });
    }
  });

  return (
    <Container
      header={
        <Header variant="h2">
          {title}
        </Header>
      }
    >
      <div className="metadata-badges-container">
        {badges.map((badge, index) => (
          <MetadataBadge
            key={badge.key || index}
            label={badge.label}
            value={badge.value}
            copyable={badge.copyable}
            isLast={index === badges.length - 1}
          />
        ))}
      </div>
    </Container>
  );
};

export default ConversationDetailHeader;
